// src/pages/GeneralOverview.js
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/GeneralOverview.css";
import logoMirai from "../assets/mirai_logo.png";
import "bootstrap-icons/font/bootstrap-icons.css";
import Navbar from "../components/Navbar";
import { PLATFORM_PDF_FOOTER } from '../constants/platform';
import html2canvas from "html2canvas";

// ChartJS Imports
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
} from 'chart.js';
import { Doughnut, Bar, Pie, Radar } from 'react-chartjs-2';

// React-PDF
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFDownloadLink,
  Image,
} from "@react-pdf/renderer";

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler
);

/* =======================
   Estilos PDF
======================= */
const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 12, fontFamily: "Helvetica" },
  header: { textAlign: "center", marginBottom: 20 },
  title: { fontSize: 18, fontWeight: "bold", color: "#004d40", marginTop: 10 },
  logo: { width: 60, height: 60, margin: "0 auto" },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginTop: 15,
    marginBottom: 8,
    color: "#333",
  },
  card: {
    border: "1pt solid #ccc",
    borderRadius: 6,
    padding: 8,
    marginBottom: 10,
  },
  row: { flexDirection: "row", gap: 10 },
  col: { flex: 1 },
  small: { fontSize: 11, color: "#444" },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 40,
    right: 40,
    fontSize: 9,
    textAlign: "center",
    color: "#666",
  },
});

/* =======================
   Helpers de acceso seguro
======================= */
const pick = (obj, keys) => {
  for (const k of keys) {
    if (obj && obj[k] != null) return obj[k];
  }
  return undefined;
};
const toNumber = (val, fallback = 0) => {
  const n = Number(val);
  return Number.isFinite(n) ? n : fallback;
};

/* =======================
   Documento PDF (Resumen)
======================= */
const ResultsPDF = ({
  userData,
  radarSoftImg,
  radarHardImg,
  softTop3,
  hardTop3,
}) => {
  const date = new Date().toLocaleDateString();

  const soft = pick(userData, ["softSkillsResults", "SoftSkillsResults"]);
  const hard = pick(userData, ["hardSkillsResults", "HardSkillsResults"]);
  const vocSec = pick(userData, [
    "VocationalSecundaryResults",
    "vocationalSecundaryResults",
  ]);
  const vocUni = pick(userData, [
    "VocationalUniversityResults",
    "vocationalUniversityResults",
  ]);
  const addict = pick(userData, [
    "AddictionDetectionResults",
    "addictionDetectionResults",
  ]);
  const bully = pick(userData, ["BullyingResults", "bullyingResults"]);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Marca de agua */}
        <Image
          src={logoMirai}
          style={{
            position: "absolute",
            top: "30%",
            left: "20%",
            width: 300,
            height: 300,
            opacity: 0.1,
          }}
        />

        {/* Header */}
        <View style={styles.header}>
          <Image src={logoMirai} style={styles.logo} />
          <Text style={styles.title}>Resumen General de Encuestas</Text>
        </View>

        {/* Datos */}
        <View style={[styles.card, { marginBottom: 12 }]}>
          <Text>Estudiante: {userData?.name || "N/D"}</Text>
          <Text>Fecha: {date}</Text>
        </View>

        {/* Radares */}
        {(radarSoftImg || radarHardImg) && (
          <>
            <Text style={styles.sectionTitle}>Perfil Visual</Text>
            <View style={[styles.row, styles.card]}>
              {radarSoftImg && (
                <View style={styles.col}>
                  <Text style={styles.small}>Habilidades Blandas</Text>
                  <Image
                    src={radarSoftImg}
                    style={{ width: 250, height: 200 }}
                  />
                </View>
              )}
              {radarHardImg && (
                <View style={styles.col}>
                  <Text style={styles.small}>Inteligencias Múltiples</Text>
                  <Image
                    src={radarHardImg}
                    style={{ width: 250, height: 200 }}
                  />
                </View>
              )}
            </View>
          </>
        )}

        {/* Soft Skills */}
        {soft && (
          <>
            <Text style={styles.sectionTitle}>Habilidades Blandas</Text>
            <View style={styles.card}>
              <Text>
                Nivel Institucional: {soft.institutionalLevel || "N/D"}
              </Text>
              {typeof soft.totalScore !== "undefined" && (
                <Text>Puntaje Total: {soft.totalScore}</Text>
              )}
              {softTop3?.length > 0 && (
                <>
                  <Text style={{ marginTop: 6, fontWeight: "bold" }}>
                    Top 3 habilidades:
                  </Text>
                  {softTop3.map((s, i) => (
                    <Text key={i}>
                      • {s.name}: {s.score} pts ({s.level})
                    </Text>
                  ))}
                </>
              )}
            </View>
          </>
        )}

        {/* Hard Skills */}
        {hard && (
          <>
            <Text style={styles.sectionTitle}>Inteligencias Múltiples</Text>
            <View style={styles.card}>
              {hardTop3?.length > 0 ? (
                <>
                  <Text style={{ fontWeight: "bold" }}>
                    Top 3 inteligencias:
                  </Text>
                  {hardTop3.map((h, i) => (
                    <Text key={i}>
                      • {h.name}: {h.score} pts {h.level ? `(${h.level})` : ""}
                    </Text>
                  ))}
                </>
              ) : (
                <Text>Se encontraron resultados, ver detalle en el panel.</Text>
              )}
            </View>
          </>
        )}

        {/* Vocacional Secundaria */}
        {vocSec && (
          <>
            <Text style={styles.sectionTitle}>
              Orientación Vocacional (Secundaria)
            </Text>
            <View style={styles.card}>
              <Text>
                Área con mayor afinidad: {vocSec.bestArea} ({vocSec.bestScore}{" "}
                pts)
              </Text>
            </View>
          </>
        )}

        {/* Vocacional Universidad */}
        {vocUni && (
          <>
            <Text style={styles.sectionTitle}>
              Orientación Vocacional (Universidad)
            </Text>
            <View style={styles.card}>
              <Text>
                Área con mayor afinidad: {vocUni.bestArea} ({vocUni.bestScore}{" "}
                pts)
              </Text>
            </View>
          </>
        )}

        {/* Adicciones */}
        {addict && (
          <>
            <Text style={styles.sectionTitle}>Detección de Adicciones</Text>
            <View style={styles.card}>
              <Text>
                Nivel de Riesgo: {addict.riskLevel} ({addict.totalScore} pts)
              </Text>
            </View>
          </>
        )}

        {/* Bullying */}
        {bully && (
          <>
            <Text style={styles.sectionTitle}>
              Acoso, Violencia y Bullying
            </Text>
            <View style={styles.card}>
              <Text>
                Nivel de Riesgo: {bully.riskLevel} ({bully.totalScore} pts)
              </Text>
            </View>
          </>
        )}

        {/* Footer */}
        <Text style={styles.footer}>
          {PLATFORM_PDF_FOOTER}
        </Text>
      </Page>
    </Document>
  );
};

/* =======================
   Componente principal
======================= */
const GeneralOverview = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSidebar, setShowSidebar] = useState(false);
  const [radarSoftImg, setRadarSoftImg] = useState(null);
  const [radarHardImg, setRadarHardImg] = useState(null);

  const navigate = useNavigate();

  // Refs para capturar los radar charts
  const softRef = useRef(null);
  const hardRef = useRef(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/users/me`,
          { headers: { Authorization: token } }
        );
        setUserData(response.data);
      } catch (error) {
        console.error("Error al obtener datos del usuario:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, []);

  // Construcción de datos para gráficos
  const soft = pick(userData || {}, ["softSkillsResults", "SoftSkillsResults"]);
  const hard = pick(userData || {}, ["hardSkillsResults", "HardSkillsResults"]);
  const addiction = pick(userData || {}, ["AddictionDetectionResults", "addictionDetectionResults"]);
  const bullying = pick(userData || {}, ["BullyingResults", "bullyingResults"]);
  const vocationalSec = pick(userData || {}, ["VocationalSecundaryResults", "vocationalSecundaryResults"]);
  const vocationalUni = pick(userData || {}, ["VocationalUniversityResults", "vocationalUniversityResults"]);

  // 1. Datos para Radar Charts (Mantener Soft y Hard Skills)
  const softRadarData = { labels: [], datasets: [] };
  const hardRadarData = { labels: [], datasets: [] };

  if (soft?.results) {
    const labels = Object.keys(soft.results);
    const data = labels.map(k => toNumber(soft.results[k]?.score, 0));
    softRadarData.labels = labels;
    softRadarData.datasets = [{
      label: 'Habilidades Blandas',
      data: data,
      backgroundColor: 'rgba(111, 66, 193, 0.2)',
      borderColor: 'rgba(111, 66, 193, 1)',
      borderWidth: 2,
    }];
  }

  if (hard) {
    const labels = [];
    const data = [];
    for (const k of Object.keys(hard)) {
      const v = hard[k];
      if (v && typeof v === "object") {
        // Limpiar nombre "Inteligencia ..."
        const name = k.replace(/^Inteligencia\s+/i, "");
        labels.push(name);
        data.push(toNumber(v.score, 0));
      }
    }
    hardRadarData.labels = labels;
    hardRadarData.datasets = [{
      label: 'Inteligencias Múltiples',
      data: data,
      backgroundColor: 'rgba(32, 201, 151, 0.2)',
      borderColor: 'rgba(32, 201, 151, 1)',
      borderWidth: 2,
    }];
  }

  // 2. Gauge Chart - Detección de Adicciones (Total Score)
  const addictionScore = addiction?.totalScore || 0;
  const maxAddictionScore = 100;
  const gaugeData = {
    labels: ['Puntaje', 'Restante'],
    datasets: [{
      data: [addictionScore, Math.max(0, maxAddictionScore - addictionScore)],
      backgroundColor: [
        addictionScore < 30 ? '#28a745' : addictionScore < 70 ? '#ffc107' : '#dc3545', // Verde bajo riesgo, Rojo alto riesgo
        '#e9ecef'
      ],
      circumference: 180,
      rotation: 270,
    }]
  };

  // 3. Pie Chart - Orientación Vocacional (Distribución de puntajes por área)
  // Usar Secundaria o Universidad según disponibilidad
  const vocationalData = vocationalSec?.totalScores || vocationalUni?.totalScores || {};
  const vocationalLabels = Object.keys(vocationalData);
  const vocationalScores = vocationalLabels.map(k => toNumber(vocationalData[k]?.score, 0));

  const pieData = {
    labels: vocationalLabels,
    datasets: [{
      data: vocationalScores,
      backgroundColor: [
        '#0d6efd', '#6610f2', '#6f42c1', '#d63384', '#dc3545',
        '#fd7e14', '#ffc107', '#198754', '#20c997', '#0dcaf0'
      ],
      borderWidth: 1,
    }]
  };

  // 4. Bar Chart - Acoso y Violencia (Bullying) - Total Score
  const bullyingScore = bullying?.totalScore || 0;
  const maxBullyingScore = 120; // Asumiendo max 120 según BullyingResults.js

  const barData = {
    labels: ['Nivel de Riesgo (Bullying)'],
    datasets: [{
      label: 'Puntaje Obtenido',
      data: [bullyingScore],
      backgroundColor: bullyingScore < 30 ? 'rgba(40, 167, 69, 0.7)' : bullyingScore < 80 ? 'rgba(255, 193, 7, 0.7)' : 'rgba(220, 53, 69, 0.7)',
      borderColor: bullyingScore < 30 ? '#28a745' : bullyingScore < 80 ? '#ffc107' : '#dc3545',
      borderWidth: 1,
    }]
  };


  // Top 3 (para PDF)
  const softTop3 =
    soft?.results
      ? Object.entries(soft.results)
        .map(([name, obj]) => ({
          name,
          score: toNumber(obj?.score, 0),
          level: obj?.level || "N/D",
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 3)
      : [];

  const hardTop3 =
    hard && Object.keys(hard).length
      ? Object.entries(hard)
        .filter(([, v]) => v && typeof v === "object")
        .map(([name, obj]) => ({
          name,
          score: toNumber(obj?.score, 0),
          level: obj?.level,
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 3)
      : [];

  // Captura los radares como imagen para el PDF
  const exportRadars = async () => {
    if (softRef.current) {
      const canvas = await html2canvas(softRef.current, {
        useCORS: true,
        backgroundColor: null,
        scale: 2,
      });
      setRadarSoftImg(canvas.toDataURL("image/png"));
    }
    if (hardRef.current) {
      const canvas = await html2canvas(hardRef.current, {
        useCORS: true,
        backgroundColor: null,
        scale: 2,
      });
      setRadarHardImg(canvas.toDataURL("image/png"));
    }
  };

  // Espera a que dibujen los gráficos y captura
  useEffect(() => {
    if (!loading && (softRadarData.labels.length || hardRadarData.labels.length)) {
      const id = setTimeout(() => exportRadars(), 1000);
      return () => clearTimeout(id);
    }
  }, [loading, userData]); // eslint-disable-line

  const toggleSidebar = () => setShowSidebar(!showSidebar);
  const closeSidebar = () => setShowSidebar(false);

  // Mapeo de encuestas para tarjetas
  const baseSurveys = [
    {
      name: "Habilidades Blandas",
      keys: ["softSkillsResults", "SoftSkillsResults"],
      route: "/soft-skills-results",
      icon: "bi-person-hearts",
      color: "#6f42c1",
    },
    {
      name: "Habilidades Duras",
      keys: ["hardSkillsResults", "HardSkillsResults"],
      route: "/hard-skills-results",
      icon: "bi-hammer",
      color: "#20c997",
    },
    {
      name: "Detección de Adicciones",
      keys: ["AddictionDetectionResults", "addictionDetectionResults"],
      route: "/addiction-detection-results",
      icon: "bi-capsule",
      color: "#dc3545",
    },
    {
      name: "Acoso, Violencia y Bullying",
      keys: ["BullyingResults", "bullyingResults"],
      route: "/Bullying-results",
      icon: "bi-shield-exclamation",
      color: "#fd7e14",
    },
  ];

  // Agregar solo la encuesta vocacional que corresponda
  let surveys = [...baseSurveys];
  if (userData?.academic_level === "Secundaria") {
    surveys.push({
      name: "Vocacional Secundaria",
      keys: ["VocationalSecundaryResults", "vocationalSecundaryResults"],
      route: "/vocational-results",
      icon: "bi-mortarboard",
      color: "#0d6efd",
    });
  } else if (userData?.academic_level === "Media Superior") {
    surveys.push({
      name: "Vocacional Universidad",
      keys: ["VocationalUniversityResults", "vocationalUniversityResults"],
      route: "/vocationalUniversity-results",
      icon: "bi-book-half",
      color: "#6610f2",
    });
  }

  return (
    <>
      <Navbar showSidebar={showSidebar} toggleSidebar={toggleSidebar} />

      <div className="general-overview-container">
        {/* Layout */}
        <div className="dashboard-layout">
          <div
            className={`sidebar-overlay ${showSidebar ? "show" : ""}`}
            onClick={closeSidebar}
          ></div>

          {/* Sidebar */}
          <div className={`dashboard-sidebar ${showSidebar ? "show" : ""}`}>
            <div className="sidebar-header">
              <h3>General Overview</h3>
            </div>
            <div className="sidebar-menu">
              {surveys.map((s, i) => (
                <button
                  key={i}
                  className="menu-item"
                  onClick={() => {
                    navigate(s.route);
                    closeSidebar();
                  }}
                >
                  <i className={`bi ${s.icon}`}></i> {s.name}
                </button>
              ))}
              {/* Botón de regreso al dashboard */}
              <button
                className="menu-item return-dashboard"
                onClick={() => {
                  navigate("/dashboard");
                  closeSidebar();
                }}
              >
                <i className="bi bi-arrow-left-circle"></i>
                Volver al Dashboard
              </button>
            </div>
          </div>

          {/* Main */}
          <div className="dashboard-main">
            <div className="overview-container">
              <h2 className="overview-title">Resumen General de Encuestas</h2>

              {loading ? (
                <div className="loading-container">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                </div>
              ) : (
                <>
                  {/* Modern Grid Layout for Charts */}
                  <div className="row g-4 mb-4">
                    {/* Gauge Chart - Addiction Detection */}
                    <div className="col-md-6 col-lg-3">
                      <div className="card shadow-sm p-3 h-100 text-center">
                        <h6 className="text-muted mb-3">Riesgo de Adicciones</h6>
                        <div style={{ position: 'relative', height: '150px' }}>
                          <Doughnut data={gaugeData} options={{ maintainAspectRatio: false, cutout: '70%', plugins: { legend: { display: false }, tooltip: { enabled: false } } }} />
                          <div style={{ position: 'absolute', top: '60%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '1.5rem', fontWeight: 'bold' }}>
                            {addictionScore}
                          </div>
                        </div>
                        <p className="small text-muted mt-2">Puntaje Total (Max 100)</p>
                      </div>
                    </div>

                    {/* Pie Chart - Vocational Profile */}
                    <div className="col-md-6 col-lg-3">
                      <div className="card shadow-sm p-3 h-100">
                        <h6 className="text-muted mb-3 text-center">Perfil Vocacional</h6>
                        <div style={{ height: '150px' }}>
                          <Pie data={pieData} options={{ maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 10 } } } } }} />
                        </div>
                      </div>
                    </div>

                    {/* Bar Chart - Bullying Risk */}
                    <div className="col-md-12 col-lg-6">
                      <div className="card shadow-sm p-3 h-100">
                        <h6 className="text-muted mb-3">Riesgo de Acoso y Bullying</h6>
                        <div style={{ height: '150px' }}>
                          <Bar
                            data={barData}
                            options={{
                              maintainAspectRatio: false,
                              indexAxis: 'y',
                              scales: { x: { max: 120, beginAtZero: true } },
                              plugins: { legend: { display: false } }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Radar Charts Row */}
                  <div className="row g-4 mb-4">
                    <div className="col-md-6">
                      <div className="card shadow-sm p-3 h-100" ref={softRef}>
                        <h6 className="text-muted mb-3 text-center">Perfil de Habilidades Blandas</h6>
                        <div style={{ height: '300px' }}>
                          <Radar data={softRadarData} options={{ maintainAspectRatio: false, scales: { r: { suggestedMin: 0, suggestedMax: 120 } } }} />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card shadow-sm p-3 h-100" ref={hardRef}>
                        <h6 className="text-muted mb-3 text-center">Perfil de Inteligencias Múltiples</h6>
                        <div style={{ height: '300px' }}>
                          <Radar data={hardRadarData} options={{ maintainAspectRatio: false, scales: { r: { suggestedMin: 0, suggestedMax: 25 } } }} />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Survey Cards Grid */}
                  <h5 className="mb-3">Detalle de Encuestas</h5>
                  <div className="survey-cards">
                    {surveys.map((survey, index) => {
                      const result = pick(userData || {}, survey.keys);
                      const completed = !!result;
                      const mainText = completed
                        ? result.bestArea ||
                        result.institutionalLevel ||
                        result.riskLevel ||
                        "Completado"
                        : "Pendiente";

                      return (
                        <div
                          key={index}
                          className="survey-card shadow"
                          style={{ borderTop: `5px solid ${survey.color}` }}
                        >
                          <div className="icon-container mb-2">
                            <i
                              className={`bi ${survey.icon} icon-big`}
                              style={{
                                color: survey.color,
                                fontSize: "3rem",
                              }}
                            ></i>
                          </div>
                          <h5 className="mb-2">{survey.name}</h5>
                          <div className="status-text mb-2">
                            {completed ? (
                              <span className="badge bg-success">
                                Completado
                              </span>
                            ) : (
                              <span className="badge bg-warning text-dark">
                                Pendiente
                              </span>
                            )}
                          </div>
                          <p className="result-summary">{mainText}</p>
                          <Link
                            to={survey.route}
                            className="btn btn-outline-primary mt-2"
                          >
                            Ver detalles
                          </Link>
                        </div>
                      );
                    })}
                  </div>

                  {/* Botones de acciones */}
                  <div className="d-flex justify-content-center gap-2 mt-4">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={exportRadars}
                      title="Volver a capturar las imágenes de los radares"
                    >
                      <i className="bi bi-arrow-clockwise"></i> Actualizar
                      gráficos
                    </button>

                    <PDFDownloadLink
                      document={
                        <ResultsPDF
                          userData={userData}
                          radarSoftImg={radarSoftImg}
                          radarHardImg={radarHardImg}
                          softTop3={softTop3}
                          hardTop3={hardTop3}
                        />
                      }
                      fileName={`Resumen_General_${new Date()
                        .toLocaleDateString()
                        .replaceAll("/", "_")}.pdf`}
                    >
                      {({ loading }) =>
                        loading ? (
                          <button className="btn btn-secondary">
                            Generando PDF...
                          </button>
                        ) : (
                          <button className="btn btn-primary">
                            <i className="bi bi-download"></i> Descargar Resumen
                          </button>
                        )
                      }
                    </PDFDownloadLink>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GeneralOverview;
